import React, { FC, memo, useEffect, useState } from 'react';
import { BrandButton } from '../../../../../everydoggyV2/brandButton/brandButton';
import locales from '../../../../../../content.json';
import { CardNumberElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toggleLoader, updatePurchaseFlow } from '../../../../../../reduxStore/navigation/actions';
import { useDispatch, useSelector } from 'react-redux';
import { IStripeViewModel } from '../../viewModel';
import { PaymentActionResult } from '../../types';
import {
  analyticsEventsTracker,
  EVENT_TRACKERS,
  GTMEvent,
  GTMQuizFunnelEvent,
} from '../../../../../../utils/analytics';
import { RootState } from '../../../../../../store';
import { getOnboardingAnswersFromURL, getParameterFromURL } from '../../../../../../utils/scripts';
import { getLocalizedError } from '../../../../../../utils/scripts/getLocalizedError';
import s from './index.module.scss';

type Props = {
  disabled?: boolean;
  email: string;
  amount?: number;
  viewModel: IStripeViewModel;
  setError?: React.Dispatch<React.SetStateAction<string>>;
};

const GetPlanButton: FC<Props> = props => {
  const { disabled, email, amount, viewModel, setError } = props;
  const stripeAmount = useSelector((state: RootState) => {
    return state.data.paymentModal.product.details ? state.data.paymentModal.product.details.amount : '';
  });
  const stripeTrialAmount = useSelector((state: RootState) => {
    return state.data.paymentModal.product.details ? state.data.paymentModal.product.details.trial_amount : '';
  });
  const amountValue = stripeTrialAmount || stripeAmount;
  const stripelName = useSelector((state: RootState) => {
    return state.data.paymentModal.product ? state.data.paymentModal.productName?.name : '';
  });

  const [answers, setAnswers] = useState(() => getOnboardingAnswersFromURL());
  const role = getParameterFromURL('role');
  const config = getParameterFromURL('config');
  const PDF = getParameterFromURL('PDF');
  const everydoggyV5 = getParameterFromURL('everydoggyV5');
  const testChat = getParameterFromURL('testChat') === 'true';
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  useEffect(() => {
    setAnswers(getOnboardingAnswersFromURL());
  }, [role, config]);

  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const showLoader = payload => {
    dispatch(toggleLoader(payload));
  };

  const togglePurchaseFlow = (email: string, url: string, payment_system: string, testChat: boolean) => {
    dispatch(updatePurchaseFlow({ email: email, url: url, payment_system: payment_system, testChat: testChat }));
  };

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setDisableSubmitButton(true);
    const event2 = GTMQuizFunnelEvent.tryPaid({
      paymentType: 'stripe',
      trialPrice: `${amountValue}`,
      productName: `${stripelName}`,
    });
    analyticsEventsTracker.trackWithTarget(event2, EVENT_TRACKERS.GTM);

    event.stopPropagation();

    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);

    stripe
      .createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          email,
        },
      })
      .then(result => {
        // Handle result.error or result.paymentMethod
        if (result.error) {
          viewModel.processPaymentError(result.error);
          // setError(result.error.message);
          setError(getLocalizedError(result.error.message));
          setDisableSubmitButton(false);
          return;
        }

        showLoader(true);

        const { id } = result.paymentMethod;
        const paymentMethodEmail: string | null = result.paymentMethod.billing_details.email;

        const gtmPurchaseEvent = GTMEvent.purchase({
          value: amount,
        });
        analyticsEventsTracker.trackWithTarget(gtmPurchaseEvent, EVENT_TRACKERS.GTM);
        window.userEmailValue = email;
        viewModel
          .processPaymentAction(id, paymentMethodEmail || email, amountValue, stripelName, answers, stripe)
          .then((requestPaymentActionResult: PaymentActionResult) => {
            setTimeout(() => {
              const urlProductName = stripelName.replace(/ /g, '_').replace(/\$/g, 'usd');
              const url = `${requestPaymentActionResult.url}&ps=stripe&pn=${urlProductName}`;
              // window.location.replace(url);
              showLoader(false);
              togglePurchaseFlow(email, url, 'cart', testChat ? true : false);
              setDisableSubmitButton(false);
            }, 1000);
          })
          .catch(error => {
            showLoader(false);
            setError(getLocalizedError(error.message));
            setDisableSubmitButton(false);
          });
      });
  };

  return (
    <>
      <div className={s.buttonWrapper}>
        <BrandButton
          data-test="pay-button"
          onClick={handleSubmit}
          type="submit"
          disabled={disabled || disableSubmitButton}
          animateButton={!disabled}
          noShadows={true}
        >
          <p>{locales[window.userLang].payment_text_44}</p>
        </BrandButton>
      </div>
    </>
  );
};

export default memo(GetPlanButton);
